// @flow
import BaseModel from "../base/BaseModel";
import LinkModel from "../links/LinkModel";
import LinkCollection from "../links/LinkCollection";
import ConceptTypeDetailModel from "./ConceptTypeDetailModel";
import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";

import type {
  IModelWithChildModels,
  ModelOptions,
  ModularUIModel,
  SectionFragment,
} from "../types";
import type Href from "../href/Href";

/**
 * Link to a concept
 */
export default class ConceptLinkModel
  extends BaseModel
  implements IModelWithChildModels
{
  _links: ?LinkCollection;
  _entryDate: ?ISO_DATE;
  _conceptType: ?ConceptTypeDetailModel;

  /**
   * Construct ConceptLinkModel
   */
  constructor(
    data: Object,
    entryDate: ?ISO_DATE = null,
    modelOptions?: ModelOptions,
  ) {
    super(data, {}, modelOptions);

    this._entryDate = entryDate;
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    if (this.conceptTypeLink) {
      this.conceptTypeLink.isCacheable = true;
      return [this.conceptTypeLink];
    }

    return [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    if (this.conceptTypeLink) {
      const href = this.conceptTypeLink.href;
      const conceptTypeModel = models.find((model) =>
        model.selfhref.equalsWithParameters(href),
      );

      if (conceptTypeModel?.type === "ConceptTypeDetail") {
        this.conceptType = conceptTypeModel;
      }
    }
  }

  /**
   * Retrieve concept type
   */
  get conceptType(): ?ConceptTypeDetailModel {
    return this._conceptType;
  }

  /**
   * Set concept type
   */
  set conceptType(conceptType: ?ModularUIModel) {
    this._conceptType =
      conceptType instanceof ConceptTypeDetailModel ? conceptType : null;
  }

  /**
   * Retrieve key
   */
  get key(): string {
    return this.data._id;
  }

  /**
   * Retrieve label
   */
  get label(): string {
    const label = this.getData("label");

    if (label) {
      return label;
    }

    return this.getData("conceptLabel", "");
  }

  /**
   * Getting the links of the resource
   */
  get links(): LinkCollection {
    if (!this._links) {
      this._links = new LinkCollection(
        Array.isArray(this.data._links)
          ? this.data._links[0]
          : this.data._links,
        null,
        this.modelOptions,
      );
    }

    return this._links;
  }

  /**
   * Get self link of model
   */
  get selflink(): LinkModel {
    const selflink = this.links.getLinkByKey("self");

    if (selflink === null) {
      throw new Error(
        `Could not find self link for ${
          this.key === null ? "unknown" : this.key
        }`,
      );
    }

    return selflink;
  }

  /**
   * Self href of concept
   */
  get selfhref(): Href {
    if (this._entryDate !== null) {
      return this.selflink.href.addParameter(
        TIMEVERSION_FILTER_NAME,
        this._entryDate,
      );
    }

    return this.selflink.href;
  }

  /**
   */
  get conceptTypeLink(): LinkModel | null {
    return this.links.getLinkByKey("concepttype");
  }

  /**
   * Concept type href of concept
   */
  get concepttypeHref(): Href | null {
    return this.conceptTypeLink?.href ?? null;
  }

  /**
   */
  get taxonomyType(): string {
    return "default";
  }

  /**
   */
  asLinkModel(): LinkModel {
    const link = LinkModel.create(
      this.key,
      this.selfhref.href,
      this.label,
      this.modelOptions,
    );
    link.href = this.selfhref;

    return link;
  }

  /**
   * Indicates if the given concept type id or href exists in the hierarchy of concept types for this concept
   */
  isOfConceptType(conceptTypeId: string): boolean {
    return this.conceptType?.isOfConceptType(conceptTypeId) ?? false;
  }

  /**
   *
   */
  get modelCategory(): string {
    return this.getData("modelCategory");
  }

  /**
   */
  get sectionId(): string {
    return this.data.section?._id;
  }

  /**
   *
   */
  get fragment(): SectionFragment {
    return this.data.section?.fragment;
  }
}
